import type { GameEvent } from "@engine/games/recordEvent.js";
import { REDACT_DECK, type RedactRound } from "./decks.js";

export type GameContext = { emit: (event: GameEvent) => void; done: () => void };

type TokenButton = {
  button: HTMLButtonElement;
  tokenIndex: number;
};

function redactedText(text: string): string {
  return "█".repeat(text.length);
}

export function mountRedact(
  root: HTMLElement,
  ctx: GameContext,
  roundIndex: number = 0,
  deck: readonly RedactRound[] = REDACT_DECK
): () => void {
  const safeIdx = deck.length > 0 ? ((roundIndex % deck.length) + deck.length) % deck.length : 0;
  const round = deck[safeIdx];
  if (!round) {
    ctx.done();
    return () => {};
  }

  const redacted = round.tokens.map(() => false);
  let submitted = false;

  root.innerHTML = "";

  const title = document.createElement("h1");
  title.textContent = "Redact Before Send";

  const card = document.createElement("div");
  card.className = "card";

  const context = document.createElement("p");
  context.className = "rd-context";
  context.textContent = round.context;

  const draft = document.createElement("div");
  draft.className = "rd-draft";
  draft.setAttribute("aria-label", "Draft message tokens");

  const buttons: TokenButton[] = [];
  for (let idx = 0; idx < round.tokens.length; idx++) {
    const token = round.tokens[idx]!;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "rd-token";
    button.dataset.original = token.text;
    button.dataset.sensitive = String(token.sensitive);
    button.dataset.conceptId = token.conceptId;
    button.dataset.index = String(idx);
    button.textContent = token.text;
    button.setAttribute("aria-pressed", "false");
    button.addEventListener("click", () => {
      if (submitted) return;
      redacted[idx] = !redacted[idx];
      button.classList.toggle("redacted", redacted[idx]);
      button.textContent = redacted[idx] ? redactedText(token.text) : token.text;
      button.setAttribute("aria-pressed", String(redacted[idx]));
    });
    draft.appendChild(button);
    buttons.push({ button, tokenIndex: idx });
  }

  const submit = document.createElement("button");
  submit.type = "button";
  submit.className = "btn-primary rd-submit";
  submit.textContent = "Submit";

  submit.addEventListener("click", () => {
    if (submitted) return;
    submitted = true;
    submit.disabled = true;
    const weight = 1 / round.tokens.length;
    for (const item of buttons) {
      const token = round.tokens[item.tokenIndex]!;
      const playerRedacted = redacted[item.tokenIndex];
      const correct = playerRedacted === token.sensitive;
      item.button.disabled = true;
      if (correct) {
        item.button.classList.add("rd-correct");
      } else if (token.sensitive && !playerRedacted) {
        item.button.classList.add("rd-missed");
      } else {
        item.button.classList.add("rd-over");
      }
      ctx.emit({
        conceptId: token.conceptId,
        correct,
        weight,
        gameType: "redact",
      });
    }
    ctx.done();
  });

  card.append(context, draft, submit);
  root.append(title, card);

  return () => {
    submitted = true;
    root.innerHTML = "";
  };
}
